import { useState } from 'react';

/**
 * Custom Hook for Working List Management
 * Handles all working list operations and state
 */
const useWorkingLists = (currentUser) => {
  const [parameterLists, setParameterLists] = useState([
    {
      id: 1,
      name: 'Favoriten',
      isPrivate: true,
      isDefault: true,
      createdBy: currentUser,
      parameterIds: [1, 2, 4, 8],
      additionRequests: [],
    },
    {
      id: 2,
      name: 'Meine Studie 2024',
      isPrivate: true,
      isDefault: false,
      createdBy: currentUser,
      parameterIds: [1, 3, 7],
      additionRequests: [],
    },
    {
      id: 3,
      name: 'Standard ICU Parameter',
      isPrivate: false,
      isDefault: false,
      createdBy: 'Data Steward Team',
      parameterIds: [1, 2, 3, 7],
      additionRequests: [{ parameterId: 6, requestedBy: 'Anna Schmidt' }],
    },
    {
      id: 4,
      name: 'Sepsis-Marker',
      isPrivate: false,
      isDefault: false,
      createdBy: 'Prof. Weber',
      parameterIds: [4, 8],
      additionRequests: [],
    },
    {
      id: 5,
      name: 'Kardio-Überwachung',
      isPrivate: false,
      isDefault: false,
      createdBy: 'Kardio Team',
      parameterIds: [1, 2],
      additionRequests: [{ parameterId: 7, requestedBy: currentUser }],
    },
  ]);

  /**
   * Check if parameter is in any list
   */
  const isParameterInAnyList = (parameterId) => {
    if (!parameterLists) return false;
    return parameterLists.some(list => list.parameterIds.includes(parameterId));
  };

  /**
   * Check if parameter is in active working list
   */
  const isParameterInActiveList = (parameterId, activeWorkingList) => {
    if (!parameterLists || !activeWorkingList || activeWorkingList.length === 0)
      return false;
    return activeWorkingList.some(listId => {
      const list = parameterLists.find(l => l.id === listId);
      return list && list.parameterIds.includes(parameterId);
    });
  };

  /**
   * Check if parameter has pending approval in active list
   */
  const hasParameterPendingInActiveList = (parameterId, activeWorkingList) => {
    if (!parameterLists || !activeWorkingList || activeWorkingList.length === 0)
      return false;
    return activeWorkingList.some(listId => {
      const list = parameterLists.find(l => l.id === listId);
      return (
        list &&
        list.additionRequests.some(req => req.parameterId === parameterId)
      );
    });
  };

  /**
   * Check if parameter is in specific list
   */
  const isParameterInList = (parameterId, listId) => {
    if (!parameterLists) return false;
    const list = parameterLists.find(l => l.id === listId);
    return list ? list.parameterIds.includes(parameterId) : false;
  };

  /**
   * Check if parameter has pending approval for specific list
   */
  const hasParameterPendingApproval = (parameterId, listId) => {
    if (!parameterLists) return false;
    const list = parameterLists.find(l => l.id === listId);
    return list
      ? list.additionRequests.some(req => req.parameterId === parameterId)
      : false;
  };

  /**
   * Add parameter to list
   */
  const handleAddToList = (parameterId, listId) => {
    if (!parameterLists) return { success: false, message: 'Listen nicht geladen' };

    const list = parameterLists.find(l => l.id === listId);
    if (!list) return { success: false, message: 'Liste nicht gefunden' };

    // Check if already in list
    if (list.parameterIds.includes(parameterId)) {
      return { success: false, message: 'Bereits in Liste enthalten', info: true };
    }

    // Update state: Add parameter to list
    setParameterLists(prevLists =>
      prevLists.map(l =>
        l.id === listId
          ? { ...l, parameterIds: [...l.parameterIds, parameterId] }
          : l
      )
    );

    return { success: true, message: `Zur Liste "${list.name}" hinzugefügt` };
  };

  /**
   * Remove parameter from list
   */
  const handleRemoveFromList = (parameterId, listId) => {
    if (!parameterLists) return { success: false, message: 'Listen nicht geladen' };

    const list = parameterLists.find(l => l.id === listId);
    if (!list) return { success: false, message: 'Liste nicht gefunden' };

    // Update state: Remove parameter from list
    setParameterLists(prevLists =>
      prevLists.map(l =>
        l.id === listId
          ? { ...l, parameterIds: l.parameterIds.filter(id => id !== parameterId) }
          : l
      )
    );

    return { success: true, message: `Aus Liste "${list.name}" entfernt` };
  };

  /**
   * Toggle parameter in list (add or remove)
   */
  const handleToggleParameterInList = (parameterId, listId) => {
    if (!parameterLists) return { success: false, message: 'Listen nicht geladen' };

    const list = parameterLists.find(l => l.id === listId);
    if (!list) return { success: false, message: 'Liste nicht gefunden' };

    const isInList = isParameterInList(parameterId, listId);
    const hasPending = hasParameterPendingApproval(parameterId, listId);

    // Private list - direct add/remove
    if (list.isPrivate && list.createdBy === currentUser) {
      if (isInList) {
        return handleRemoveFromList(parameterId, listId);
      } else {
        return handleAddToList(parameterId, listId);
      }
    }

    // Public list - requires approval
    if (!list.isPrivate) {
      if (isInList) {
        return {
          success: false,
          message: 'Aus öffentlichen Listen kann nicht entfernt werden',
          info: true,
        };
      }

      if (hasPending) {
        return {
          success: false,
          message: 'Vorschlag wurde bereits gesendet',
          info: true,
        };
      }

      // Send addition request - add to additionRequests array
      setParameterLists(prevLists =>
        prevLists.map(l =>
          l.id === listId
            ? {
                ...l,
                additionRequests: [
                  ...l.additionRequests,
                  { parameterId, requestedBy: currentUser }
                ]
              }
            : l
        )
      );

      return {
        success: true,
        message: `Vorschlag für "${list.name}" wurde gesendet`,
        info: true,
      };
    }

    return { success: false, message: 'Unbekannter Fehler' };
  };

  return {
    parameterLists,
    setParameterLists,
    isParameterInAnyList,
    isParameterInActiveList,
    hasParameterPendingInActiveList,
    isParameterInList,
    hasParameterPendingApproval,
    handleAddToList,
    handleRemoveFromList,
    handleToggleParameterInList,
  };
};

export default useWorkingLists;
